import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QRCodeSVG } from "qrcode.react";
import { Star, Copy, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSiteSettings } from "@/hooks/useSiteSettings";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId?: string | null;
}

const SITE_BASE = typeof window !== "undefined"
  ? window.location.origin
  : "https://www.inparadisebeauty.com";

export const ReviewQRDialog = ({ open, onOpenChange, bookingId }: Props) => {
  const { toast } = useToast();
  const { settings } = useSiteSettings();
  const reviewUrl = `${SITE_BASE}/review${bookingId ? `/${bookingId}` : ""}`;
  const googleEnabled = settings.google_reviews_enabled === "true";
  const googleUrl = googleEnabled ? settings.google_review_url?.trim() : "";

  const copy = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ title: "Link copied", description: "Share with your client." });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none border border-primary/30 bg-card">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-2 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Star size={22} className="text-primary fill-primary" />
          </div>
          <DialogTitle className="font-serif text-2xl italic font-bold text-center">
            Loved your visit?
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground font-light">
            Scan this QR to leave a quick review — and add up to 3 photos of your nails.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-4">
          <div className="p-4 bg-white border border-border">
            <QRCodeSVG value={reviewUrl} size={200} level="M" includeMargin={false} />
          </div>
          <p className="text-xs text-muted-foreground text-center break-all max-w-full px-4">
            {reviewUrl}
          </p>
          <div className="flex gap-2 w-full">
            <Button
              variant="outline"
              className="flex-1 rounded-none text-xs tracking-widest uppercase"
              onClick={() => copy(reviewUrl)}
            >
              <Copy size={14} className="mr-2" /> Copy Link
            </Button>
            <Button
              className="flex-1 rounded-none text-xs tracking-widest uppercase"
              onClick={() => window.open(reviewUrl, "_blank")}
            >
              <ExternalLink size={14} className="mr-2" /> Open
            </Button>
          </div>

          {googleUrl && (
            <a
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-3 border border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors text-xs tracking-[0.25em] uppercase"
            >
              ⭐ Leave a Google Review
            </a>
          )}
        </div>

        <button
          onClick={() => onOpenChange(false)}
          className="text-xs text-muted-foreground hover:text-foreground tracking-widest uppercase mt-2"
        >
          Maybe later
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewQRDialog;
